/** @format */

import { computed, effect, inject, Injectable, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import {
	CertonaRecommendations,
	CertonaRecommendationsResponse,
	CertonaScheme
} from '@shared/data-access/interfaces';
import { SchemeStatus } from '../interfaces/scheme-status.interface';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
	providedIn: 'root'
})
export class CertonaService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly ngZone = inject(NgZone);

	// Keep existing subject for backward compatibility
	private readonly certonaRecommendationsSubject =
		new BehaviorSubject<CertonaRecommendationsResponse>(undefined);
	public certonaRecommendationsState = this.certonaRecommendationsSubject.asObservable();

	// New signal-based state for schemes
	private readonly schemesSignal = signal<Record<string, CertonaScheme>>({});

	/**
	 * Have we received any recommendations data at all?
	 * Monetate excludes schemes instead of sending empty ones, so we need this to distinguish between
	 * "waiting for data" and "data does not include this scheme"
	 */
	private readonly isInitialized = signal(false);

	private readonly resonanceReadySubject = new BehaviorSubject<{ isPLAEnabled: boolean }>(
		undefined
	);
	public readonly resonanceReadyState = this.resonanceReadySubject.asObservable();
	private readonly cartCertonaReadySubject = new BehaviorSubject<any>(undefined);
	public readonly cartCertonaReadyState = this.cartCertonaReadySubject.asObservable();

	private readonly _showLoadingIndicators = signal(true);
	public readonly showLoadingIndicators = this._showLoadingIndicators.asReadonly();

	private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			this.ngZone.runOutsideAngular(() => {
				this.loadingTimeoutId = setTimeout(() => {
					this.ngZone.run(() => {
						this._showLoadingIndicators.set(false);
					});
				}, DCSG_CONSTANTS.productRecs.timeout);
			});
		}

		effect(() => {
			if (this.isInitialized()) {
				if (this.loadingTimeoutId) {
					clearTimeout(this.loadingTimeoutId);
					this.loadingTimeoutId = null;
				}
				this._showLoadingIndicators.set(false);
			}
		});
	}

	/**
	 * Get a specific scheme by name
	 * @param schemeName The name of the scheme to retrieve
	 * @returns A computed signal containing the scheme or null if not found
	 */
	getScheme(schemeName: string) {
		return computed(() => this.schemesSignal()[schemeName] || null);
	}

	/**
	 * Check if a scheme exists and has displayable content
	 * @param schemeName The name of the scheme to check
	 * @returns A computed signal that's true when the scheme exists with display: 'yes' and items
	 */
	getSchemeStatus(schemeName: string) {
		return computed((): SchemeStatus => {
			const isInitialized = this.isInitialized();
			const scheme = isInitialized ? this.getScheme(schemeName)() : undefined;
			const isValid = !!scheme && Array.isArray(scheme.items) && scheme.items.length > 0;
			const isDisplayable = isValid && scheme.display === 'yes';
			return {
				initialized: isInitialized,
				valid: isValid,
				displayable: isDisplayable
			};
		});
	}

	// Observable version for backward compatibility
	getScheme$(schemeName: string): Observable<CertonaScheme | null> {
		const schemeSignal = this.getScheme(schemeName);
		return toObservable(schemeSignal);
	}

	// Observable version for backward compatibility
	getSchemeStatus$(schemeName: string): Observable<SchemeStatus> {
		const hasValidSignal = this.getSchemeStatus(schemeName);
		return toObservable(hasValidSignal);
	}

	resonanceReady(isPLAEnabled: any) {
		this.resonanceReadySubject.next({
			isPLAEnabled
		});
	}

	cartCertona(isCartCertona: any) {
		this.cartCertonaReadySubject.next({
			isCartCertona
		});
	}

	/**
	 * Set recommendations data - maintains original signature for backward compatibility
	 * @param certonaRecommendations The Certona recommendations data (any type for backward compatibility)
	 */
	recommendations(certonaRecommendations: CertonaRecommendations) {
		// Keep original behavior exactly as it was
		this.certonaRecommendationsSubject.next({
			certonaRecommendations
		});

		// Update signal-based state with new schemes (safely handle any data structure)
		if (
			certonaRecommendations?.resonance?.schemes &&
			Array.isArray(certonaRecommendations.resonance.schemes)
		) {
			// Create completely new schemes object to ensure signal reactivity
			const newSchemes: Record<string, CertonaScheme> = {
				...this.schemesSignal()
			};

			// Process each scheme in the recommendations
			for (const scheme of certonaRecommendations.resonance.schemes) {
				// Store scheme by its name for easy lookup with deep copy
				if (scheme?.scheme) {
					newSchemes[scheme.scheme] = { ...scheme };
				}
			}

			// Use .set() instead of .update() to force signal change detection
			this.schemesSignal.set(newSchemes);
		}

		// backwards compatibility with product recs component skeleton loaders
		if (certonaRecommendations && certonaRecommendations.source !== 'skeleton') {
			this.isInitialized.set(true);
		}
	}

	/**
	 * Clear all recommendations
	 */
	clearRecommendations(): void {
		this.certonaRecommendationsSubject.next(null);
		this.schemesSignal.set({});
		this.isInitialized.set(false);
	}
}
